'use client';

import { useEffect, useState } from 'react';

import type { ArticleSection } from '@eliadmin/core/src/components/dlz/blog/article/article-html';

import { cn } from '@eliadmin/core/src/utils/style-utils';

/** A section counts as read once its heading passes this line (below the sticky header). */
const READ_LINE = 160;

/**
 * „Obsah článku“ (Figma 60344:25418, phones 60460:110794): links to the article headings and the
 * products; the section being read is highlighted (bg/brand-subtle, text/brand, `aria-current`).
 */
export function TableOfContents({
  sections,
  title,
  className,
}: {
  sections: ArticleSection[];
  title: string;
  className?: string;
}) {
  const [current, setCurrent] = useState(sections[0]?.id);

  useEffect(() => {
    const targets = sections
      .map(section => document.getElementById(section.id))
      .filter(target => target !== null);
    let frame = 0;

    const update = () => {
      frame = 0;
      const read = targets.filter(target => target.getBoundingClientRect().top <= READ_LINE);
      setCurrent((read.at(-1) ?? targets[0])?.id);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [sections]);

  return (
    <nav
      aria-labelledby="article-toc-title"
      className={cn(
        'flex flex-col gap-3 rounded-[18px] border border-[#E2E4E7] bg-white p-4 lg:gap-4 lg:p-6',
        className,
      )}
    >
      <p id="article-toc-title" className="dlz-h5 text-[#0F1012]">
        {title}
      </p>
      <ol>
        {sections.map(section => {
          const active = section.id === current;
          return (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                aria-current={active ? 'location' : undefined}
                className={cn(
                  'block rounded-[10px] px-3 py-2.5 transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0F1012]',
                  active
                    ? 'dlz-body-s-medium bg-[#FDF0F0] text-[#D7191F]'
                    : 'dlz-body-s text-[#4F545C] hover:bg-[#F5F6F7] hover:text-[#0F1012]',
                )}
              >
                {section.label}
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
